"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Menu } from "lucide-react"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { useAuth } from "@/components/auth-provider"
import { useState, useEffect } from "react"
import { toast } from "sonner"

export default function Navbar() {
  const { user, loading, isSuperAdmin } = useAuth()
  const pathname = usePathname()
  const router = useRouter()
  const [currentView, setCurrentView] = useState<"marketing" | "sales">("marketing")
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [hasGoogleAdsConnection, setHasGoogleAdsConnection] = useState(false)

  // Determine if user is actually logged in (not just loading)
  const isLoggedIn = !loading && !!user

  // Check for Google Ads connection when user is logged in
  useEffect(() => {
    async function checkConnection() {
      if (user && isLoggedIn) {
        try {
          // Use secure API endpoint instead of direct client query
          const response = await fetch('/api/google-ads/connection-check')

          if (response.ok) {
            const { connected } = await response.json()
            setHasGoogleAdsConnection(connected)
          } else {
            console.error('Error checking Google Ads connection:', response.statusText)
            setHasGoogleAdsConnection(false)
          }
        } catch (error) {
          console.error('Error checking Google Ads connection:', error)
          setHasGoogleAdsConnection(false)
        }
      }
    }

    checkConnection()
  }, [user, isLoggedIn])


  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false)
      } else {
        setIsVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener("scroll", controlNavbar)
    return () => {
      window.removeEventListener("scroll", controlNavbar)
    }
  }, [lastScrollY])

  // Homepage navigation for logged-out users
  const homepageRoutes = [
    {
      href: "/",
      label: "Home",
      active: pathname === "/",
    },
    {
      href: "/about",
      label: "About",
      active: pathname === "/about",
    },
    {
      href: "/demo",
      label: "Demo",
      active: pathname === "/demo",
    },
    {
      href: "/pricing",
      label: "Pricing",
      active: pathname === "/pricing",
    },
    {
      href: "/contact",
      label: "Contact",
      active: pathname === "/contact",
    },
  ]

  // Dashboard navigation for logged-in users
  const dashboardRoutes =
    currentView === "marketing"
      ? [
          {
            href: "/dashboard",
            label: "Dashboard",
            active: pathname === "/dashboard",
          },
          // DEPRECATED: Content Studio - removed from navigation
          // Conditionally add Google Ads navigation if connected
          ...(hasGoogleAdsConnection ? [{
            href: "/dashboard/google-ads",
            label: "Google Ads",
            active: pathname === "/dashboard/google-ads",
          }] : []),
          // MOVED TO SIDEBAR: Leads - accessible via sidebar icon
          // {
          //   href: "/dashboard/leads",
          //   label: "Leads",
          //   active: pathname === "/dashboard/leads",
          // },
          {
            href: "/dashboard/crm",
            label: "CRM",
            active: pathname === "/dashboard/crm" || pathname.startsWith("/dashboard/crm/"),
          },
          {
            href: "/dashboard/analytics",
            label: "Analytics",
            active: pathname === "/dashboard/analytics",
          },
        ]
      : [
          {
            href: "/dashboard/sales/booking-agent",
            label: "Booking Agent",
            active: pathname === "/dashboard/sales/booking-agent",
          },
          {
            href: "/dashboard/sales/calendar",
            label: "Calendar",
            active: pathname === "/dashboard/sales/calendar",
          },
        ]

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <header data-app-chrome="navbar" className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-center">
          <span className="font-semibold text-base">MaiTeams</span>
        </div>
      </header>
    )
  }

  const routes = isLoggedIn ? dashboardRoutes : homepageRoutes

  return (
    <header
      data-app-chrome="navbar"
      className={`sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-transform duration-300 ${isVisible ? "translate-y-0" : "-translate-y-full"}`}
    >
      <div className="container flex h-14 items-center justify-center">
        <div className="hidden md:flex items-center">
          <nav className="flex items-center space-x-6 text-sm font-medium">
            {/* Company name as first item in nav */}
            <span className="font-semibold text-base">MaiTeams</span>
            {routes.map((route) => (
              <Link
                key={route.href}
                href={route.href}
                prefetch={route.href === "/dashboard"}
                className={`relative z-10 transition-colors hover:text-foreground/80 ${
                  route.active ? "text-foreground" : "text-foreground/60"
                }`}
                onClick={(e) => {
                  // Ensure navigation happens immediately for dashboard
                  if (route.href === "/dashboard") {
                    e.stopPropagation();
                  }
                }}
              >
                {route.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Mobile Navigation */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="mr-2 md:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="pr-0">
            {/* Mobile: Show simple logo for both logged-in and logged-out users */}
            <Link href="/" className="flex items-center space-x-2">
              <img src="/maiteams-logo.jpeg" alt="MaiTeams Logo" className="h-6 w-auto" />
              <span className="font-bold">MaiTeams</span>
            </Link>
            <nav className="mt-6 flex flex-col space-y-3">
              {routes.map((route) => (
                <Link
                  key={route.href}
                  href={route.href}
                  className={`px-2 py-1 text-sm ${route.active ? "font-medium text-primary" : "text-muted-foreground"}`}
                >
                  {route.label}
                </Link>
              ))}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Login button for logged-out users */}
        {!isLoggedIn && (
          <div className="flex flex-1 items-center justify-end">
            <Button asChild variant="default" size="sm">
              <Link href="/login">Login</Link>
            </Button>
          </div>
        )}
      </div>
    </header>
  )
}
